import { useRef, useState } from "react";

interface UseCustomLongPressOptions {
  /**
   * Time in milliseconds to wait before triggering the callback
   * @default 500
   */
  threshold?: number;
  /**
   * Movement threshold in pixels that will cancel the long press
   * @default 10
   */
  movementThreshold?: number;
}

/**
 * Custom hook for detecting long press with movement cancellation
 * @param callback Function to call when long press is detected
 * @param options Configuration options
 * @returns Touch event handlers to spread on a component
 */
export function useCustomLongPress(
  callback: () => void,
  options: UseCustomLongPressOptions = {},
): {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void; // Add this handler
} {
  const { threshold = 500, movementThreshold = 10 } = options;
  const [startPos, setStartPos] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const isMovingRef = useRef<boolean>(false); // Use a ref instead of state
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const handleTouchStart = (e: React.TouchEvent): void => {
    const touch = e.touches[0];
    if (!touch) {
      return;
    }

    // Reset moving state at the start of each touch
    isMovingRef.current = false;

    setStartPos({
      x: touch.clientX,
      y: touch.clientY,
    });

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      if (!isMovingRef.current) {
        callback();
      }
    }, threshold);
  };

  const handleTouchMove = (e: React.TouchEvent): void => {
    const touch = e.touches[0];
    if (!touch) {
      return;
    }
    const deltaX = Math.abs(touch.clientX - startPos.x);
    const deltaY = Math.abs(touch.clientY - startPos.y);

    if (deltaX > movementThreshold || deltaY > movementThreshold) {
      isMovingRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const handleTouchEnd = (): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleTouchCancel = (): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onTouchCancel: handleTouchCancel,
  };
}
